/**
 * Category URL utilities for SEO-friendly URLs
 *
 * Transforms category URLs from query-param based (/s?pub_categoryLevel1=phones)
 * to clean path-based (/c/phones/phones-iphone) for improved SEO.
 */

/**
 * Build a clean category URL from category slugs
 * @param {string} category1 - Level 1 category slug
 * @param {string} category2 - Level 2 category slug (optional)
 * @param {string} category3 - Level 3 category slug (optional)
 * @returns {string} Clean category URL path
 */
export const buildCategoryUrl = (category1, category2 = null, category3 = null) => {
  if (!category1) return '/s';

  let path = `/c/${category1}`;
  if (category2) path += `/${category2}`;
  if (category3) path += `/${category3}`;

  return path;
};

/**
 * Convert query params to a clean category URL
 * Used for redirects from old /s?pub_categoryLevel1=... URLs
 * @param {object} queryParams - Query parameters object
 * @returns {string|null} Clean category URL or null if no category params
 */
export const queryParamsToCategoryUrl = queryParams => {
  const {
    pub_categoryLevel1,
    pub_categoryLevel2,
    pub_categoryLevel3,
    ...otherParams
  } = queryParams;

  // Only convert if there are category params and no other search params
  // that would require the search page functionality
  if (!pub_categoryLevel1) return null;

  const categoryUrl = buildCategoryUrl(pub_categoryLevel1, pub_categoryLevel2, pub_categoryLevel3);

  // Build query string for remaining params (excluding category params)
  const remainingKeys = Object.keys(otherParams).filter(
    key => !key.startsWith('pub_categoryLevel')
  );

  if (remainingKeys.length > 0) {
    const searchParams = new URLSearchParams();
    remainingKeys.forEach(key => {
      if (otherParams[key] !== undefined && otherParams[key] !== null) {
        searchParams.set(key, otherParams[key]);
      }
    });
    const queryString = searchParams.toString();
    return queryString ? `${categoryUrl}?${queryString}` : categoryUrl;
  }

  return categoryUrl;
};

/**
 * Check if a URL has category query params that should be redirected
 * @param {object} queryParams - Query parameters object
 * @returns {boolean} True if URL should be redirected to clean category URL
 */
export const shouldRedirectToCategoryUrl = queryParams => {
  return !!(
    queryParams.pub_categoryLevel1 ||
    queryParams.pub_categoryLevel2 ||
    queryParams.pub_categoryLevel3
  );
};

/**
 * Get category name from slug for SEO meta tags
 * @param {string} slug - Category slug
 * @param {Array} categories - Category configuration array
 * @returns {string|null} Category name or null if not found
 */
export const getCategoryName = (slug, categories) => {
  if (!slug || !categories) return null;

  // Search through categories recursively
  const findCategory = (cats, targetSlug) => {
    for (const cat of cats) {
      if (cat.id === targetSlug) {
        return cat.name;
      }
      if (cat.subcategories) {
        const found = findCategory(cat.subcategories, targetSlug);
        if (found) return found;
      }
    }
    return null;
  };

  return findCategory(categories, slug);
};

/**
 * Build category breadcrumb from URL params
 * @param {object} params - URL params with category1, category2, category3
 * @param {Array} categories - Category configuration array
 * @returns {Array} Array of { name, url } objects for breadcrumb
 */
export const buildCategoryBreadcrumb = (params, categories) => {
  const { category1, category2, category3 } = params;
  const breadcrumb = [];

  if (category1) {
    const name = getCategoryName(category1, categories);
    if (name) {
      breadcrumb.push({
        name,
        url: buildCategoryUrl(category1),
      });
    }
  }

  if (category2) {
    const name = getCategoryName(category2, categories);
    if (name) {
      breadcrumb.push({
        name,
        url: buildCategoryUrl(category1, category2),
      });
    }
  }

  if (category3) {
    const name = getCategoryName(category3, categories);
    if (name) {
      breadcrumb.push({
        name,
        url: buildCategoryUrl(category1, category2, category3),
      });
    }
  }

  return breadcrumb;
};

/**
 * Format category slug to display name (fallback when category config not available)
 * @param {string} slug - Category slug like "phones-iphone"
 * @returns {string} Formatted name like "Phones Iphone"
 */
export const formatCategorySlug = slug => {
  if (!slug) return '';
  return slug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Check if current path is a category page
 * @param {string} pathname - Current URL pathname
 * @returns {boolean} True if path is a category page
 */
export const isCategoryPage = pathname => {
  return pathname.startsWith('/c/');
};

/**
 * Extract category params from pathname
 * @param {string} pathname - Current URL pathname
 * @returns {object} Object with category1, category2, category3
 */
export const extractCategoryFromPath = pathname => {
  if (!pathname.startsWith('/c/')) {
    return { category1: null, category2: null, category3: null };
  }

  const parts = pathname
    .replace('/c/', '')
    .split('/')
    .filter(Boolean);
  return {
    category1: parts[0] || null,
    category2: parts[1] || null,
    category3: parts[2] || null,
  };
};
